import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { showErrorToast } from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import useToggleClass from '../../../../../../common/hooks/useToggleClass';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Icons from '../../../../../../common/widgets/Icons';
import Password from '../../../../../../common/widgets/Password';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { ChangePasswordModel } from '../../../../models/Customers';
import { ValidationError } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { customerService } from '../../../../services/customer';

interface IErrorState {
  oldPassword: ValidationError[];
  newPassword: ValidationError[];
  confirmPassword: ValidationError[];
}

export const PasswordChange = () => {
  const minLengthRef = useRef<HTMLLIElement | null>(null);
  const includeSpecialCharRef = useRef<HTMLLIElement | null>(null);
  const includeNumberRef = useRef<HTMLLIElement | null>(null);
  const auth = useAuth();
  const isSavingRef = useRef(false);

  const MIN_PASSWORD_LENGTH = 12;

  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { addClass, removeClass } = useToggleClass();

  const htmlElement = document.getElementsByTagName('html')[0];

  const defaultErrorState = {
    oldPassword: [],
    newPassword: [],
    confirmPassword: [],
  };

  const [showSuccesDialog, setShowSuccesDialog] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [state, setState] = useState<ChangePasswordModel>({
    email: customerInfo.email,
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errorState, setErrorState] = useState<IErrorState>(defaultErrorState);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(
    !state.oldPassword || !state.newPassword || !state.confirmPassword,
  );

  const handleChange = (event: any) => {
    const value = event.target.value;
    const name = event.target.name;

    if (name === 'newPassword') {
      checkValidationRules(value);
    }

    setState(prevState => ({ ...prevState, [name]: value }));
    setErrorState(prevState => ({ ...prevState, [name]: [] }));
  };

  const isValidState = () => {
    setErrorState(defaultErrorState);
    let isValid = true;

    if (!state.oldPassword) {
      setErrorState(prevState => ({
        ...prevState,
        oldPassword: [
          {
            target: 'oldPassword',
            severity: 'error',
            message: translate(
              resources.admin.employees.validation.password.oldPasswordRequired,
            ),
          } as ValidationError,
        ],
      }));

      isValid = false;
    }

    if (!state.newPassword) {
      setErrorState(prevState => ({
        ...prevState,
        newPassword: [
          {
            target: 'newPassword',
            severity: 'error',
            message: translate(
              resources.admin.employees.validation.password.newPasswordRequired,
            ),
          } as ValidationError,
        ],
      }));

      isValid = false;
    }

    if (!state.confirmPassword) {
      setErrorState(prevState => ({
        ...prevState,
        confirmPassword: [
          {
            target: 'confirmPassword',
            severity: 'error',
            message: translate(
              resources.admin.employees.validation.password
                .confirmPasswordRequired,
            ),
          } as ValidationError,
        ],
      }));

      isValid = false;
    } else if (state.newPassword !== state.confirmPassword) {
      setErrorState(prevState => ({
        ...prevState,
        confirmPassword: [
          {
            target: 'confirmPassword',
            severity: 'error',
            message: translate(
              resources.admin.companies.validation.password.passwordDontMatch,
            ),
          } as ValidationError,
        ],
      }));

      isValid = false;
    }

    return isValid;
  };

  const checkValidationRules = (password: string) => {
    const specialCharacterRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/;
    const numberRegex = /\d/;
    let validationPassed = true;

    if (password.length < MIN_PASSWORD_LENGTH) {
      validationPassed = false;

      removeClass('validation-passed', minLengthRef);
      addClass('validation-failed', minLengthRef);
    } else {
      removeClass('validation-failed', minLengthRef);
      addClass('validation-passed', minLengthRef);
    }

    if (!specialCharacterRegex.test(password)) {
      validationPassed = false;

      removeClass('validation-passed', includeSpecialCharRef);
      addClass('validation-failed', includeSpecialCharRef);
    } else {
      removeClass('validation-failed', includeSpecialCharRef);
      addClass('validation-passed', includeSpecialCharRef);
    }

    if (!numberRegex.test(password)) {
      validationPassed = false;

      removeClass('validation-passed', includeNumberRef);
      addClass('validation-failed', includeNumberRef);
    } else {
      removeClass('validation-failed', includeNumberRef);
      addClass('validation-passed', includeNumberRef);
    }

    validationPassed
      ? setSaveDisabled(() => false)
      : setSaveDisabled(() => true);
  };

  const clearValidationMessages = () => {
    minLengthRef.current?.classList.remove(
      'validation-failed',
      'validation-passed',
    );
    includeSpecialCharRef.current?.classList.remove(
      'validation-failed',
      'validation-passed',
    );
    includeNumberRef.current?.classList.remove(
      'validation-failed',
      'validation-passed',
    );
  };

  const handleSave = () => {
    if (isSavingRef.current) {
      return;
    }

    if (isValidState()) {
      isSavingRef.current = true;
      setIsLoading(() => true);

      (async () => {
        const result = await customerService.updateCustomerPassword(state);

        if (!result.hasErrors && result.data.success) {
          setShowSuccesDialog(true);
          setEmailSent(result.data.emailSent);
          clearValidationMessages();
        } else {
          showErrorToast(
            translate(resources.admin.employees.changePassword.errorMessage),
          );
        }
        setIsLoading(() => false);
        isSavingRef.current = false;
      })();
    }
  };

  useMount(abortController => {
    if (!state.email) {
      (async () => {
        const result = await customerService.getWhoAmI({
          signal: abortController?.signal,
        });

        if (!result.hasErrors) {
          setState(prevState => ({ ...prevState, email: result.data.email }));
        }
      })();
    }
  });

  useEffect(() => {
    showSuccesDialog
      ? htmlElement.classList.add('dialog-active-box')
      : htmlElement.classList.remove('dialog-active-box');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccesDialog]);

  const handleLogout = async () => {
    try {
      setShowSuccesDialog(false);
      await auth.signoutRedirect();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      <div className="change-password-action">
        <Button
          className="btn btn-solid"
          icon={<Icons.Padlock />}
          onClick={handleSave}
          loading={isLoading}
          disabled={saveDisabled}
        >
          {translate(resources.changePassword.changePasswordButton)}
        </Button>
      </div>

      <div className="change-password-section">
        <div>
          <Password
            label={translate(resources.changePassword.oldPassword)}
            mandatory={true}
            value={state.oldPassword}
            name="oldPassword"
            onChange={handleChange}
            disabled={isLoading}
            autocomplete="new-password"
            errors={errorState.oldPassword}
          />
          <Password
            label={translate(resources.changePassword.newPassword)}
            mandatory
            value={state.newPassword}
            name="newPassword"
            onChange={handleChange}
            disabled={isLoading}
            autocomplete="new-password"
            errors={errorState.newPassword}
          />
          <Password
            label={translate(resources.changePassword.confirmNewPassword)}
            mandatory={true}
            value={state.confirmPassword}
            name="confirmPassword"
            onChange={handleChange}
            disabled={isLoading}
            autocomplete="new-password"
            errors={errorState.confirmPassword}
          />
        </div>

        <div>
          <h2>{translate(resources.changePassword.howToChangeTile)}</h2>
          <p>{translate(resources.changePassword.howToChangeDescription)}</p>
          <ul className="password-rules">
            <li ref={minLengthRef}>
              {translate(resources.changePassword.minLength)}
            </li>
            <li ref={includeSpecialCharRef}>
              {translate(resources.changePassword.nonAlphaNumeric)}
            </li>
            <li ref={includeNumberRef}>
              {translate(resources.changePassword.digit)}
            </li>
          </ul>
        </div>
      </div>

      {showSuccesDialog && (
        <Dialog className="dialog-s">
          <div className="dialog-content password-change-success">
            <div className="dialog-image">
              <Icons.Success />
            </div>
            <div className="dialog-title">
              {translate(resources.admin.employees.changePassword.success)}
            </div>
            <div className="dialog-text centered">
              {emailSent
                ? translate(
                    resources.admin.employees.changePassword
                      .confirmationEmailSent,
                  )
                : translate(
                    resources.admin.employees.changePassword
                      .confirmationEmailFailed,
                  )}{' '}
              {translate(
                resources.admin.employees.changePassword.successMessage,
              )}
            </div>
          </div>
          <div className="dialog-actions password-change-success">
            <Button className="btn btn-outline" onClick={handleLogout}>
              {translate(resources.admin.employees.changePassword.login)}
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
};
